import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Link, useLocation, useParams, Outlet } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { DEFAULT_LOCALE, isSupportedLocale, isRtl, type SupportedLocale } from '@noxverify/i18n';
import { CookieConsent } from './components/CookieConsent';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { ThemeSwitcher } from './components/ThemeSwitcher';
import { SEO } from './seo/SEO';
import {
  organizationLd,
  websiteLd,
  softwareApplicationLd,
  kycServiceLd,
  kybServiceLd,
  kymServiceLd,
  faqLd,
} from './seo/structured-data';

/* ═══════════════════════════════════════════════════════════════════════════
   LAZY-LOADED PAGES (code-splitting)
   ═══════════════════════════════════════════════════════════════════════════ */

const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const DpaPage = lazy(() => import('./pages/DpaPage').then((m) => ({ default: m.DpaPage })));
const CookiesPage = lazy(() =>
  import('./pages/CookiesPage').then((m) => ({ default: m.CookiesPage })),
);
const AupPage = lazy(() => import('./pages/AupPage').then((m) => ({ default: m.AupPage })));
const SlaPage = lazy(() => import('./pages/SlaPage').then((m) => ({ default: m.SlaPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })),
);
const CareersPage = lazy(() =>
  import('./pages/CareersPage').then((m) => ({ default: m.CareersPage })),
);
const BlogPage = lazy(() => import('./pages/BlogPage').then((m) => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() =>
  import('./pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage })),
);
const ApiDocsPage = lazy(() =>
  import('./pages/ApiDocsPage').then((m) => ({ default: m.ApiDocsPage })),
);
const ChangelogPage = lazy(() =>
  import('./pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage })),
);
const CompliancePage = lazy(() =>
  import('./pages/CompliancePage').then((m) => ({ default: m.CompliancePage })),
);
const TrustPage = lazy(() => import('./pages/TrustPage').then((m) => ({ default: m.TrustPage })));
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then((m) => ({ default: m.StatusPage })),
);

/**
 * Eagerly load every code-split page module. Used ONLY by the build-time SSR
 * entry (`entry-server.tsx`) to settle `React.lazy` payloads before
 * `renderToString`, so prerendered HTML contains real page body content. This
 * is never called on the client, so it does not affect runtime code-splitting.
 */
export function warmupRoutes(): Promise<unknown> {
  return Promise.all([
    import('./pages/TermsPage'),
    import('./pages/PrivacyPage'),
    import('./pages/DpaPage'),
    import('./pages/CookiesPage'),
    import('./pages/AupPage'),
    import('./pages/SlaPage'),
    import('./pages/AboutPage'),
    import('./pages/ContactPage'),
    import('./pages/CareersPage'),
    import('./pages/BlogPage'),
    import('./pages/BlogPostPage'),
    import('./pages/ApiDocsPage'),
    import('./pages/ChangelogPage'),
    import('./pages/CompliancePage'),
    import('./pages/TrustPage'),
    import('./pages/StatusPage'),
  ]);
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════ */

const SIGN_IN_URL = 'https://admin.noxverify.com';
const CONTACT_EMAIL = 'hello@noxverify.com';
const FOCUS_VISIBLE_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

const SECTION_IDS = ['features', 'how-it-works', 'compliance', 'pricing', 'demo'] as const;

/* ═══════════════════════════════════════════════════════════════════════════
   LOCALE-AWARE HOME LINK
   ═══════════════════════════════════════════════════════════════════════════ */

/** Build a localized in-app path, respecting current i18n language. */
function useLocalizedPath(): (path: string) => string {
  const { i18n } = useTranslation();
  const rawLng = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  const lng = (isSupportedLocale(rawLng) ? rawLng : DEFAULT_LOCALE) as SupportedLocale;
  return useCallback(
    (path: string) => {
      if (lng === DEFAULT_LOCALE) return path;
      if (path === '/') return `/${lng}`;
      if (path.startsWith('#')) return path; // pure hash links stay as-is
      return `/${lng}${path}`;
    },
    [lng],
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LOGO
   ═══════════════════════════════════════════════════════════════════════════ */

function LogoMark({ size = 36 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect width="36" height="36" rx="8" fill="#1B57D6" />
      <path
        d="M11 25.5V10.5L25 25.5V10.5"
        stroke="#FFFFFF"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Logo({ light = false }: { light?: boolean }) {
  const location = useLocation();
  const localize = useLocalizedPath();
  const homePath = localize('/');

  const handleClick = (e: React.MouseEvent) => {
    if (location.pathname === homePath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Link
      to={homePath}
      onClick={handleClick}
      className={`flex items-center gap-2.5 group rounded-md ${FOCUS_VISIBLE_RING}`}
      aria-label="NoxVerify - go to home page"
    >
      <LogoMark size={34} />
      <span
        className={`font-display text-xl font-semibold tracking-tight ${light ? 'text-surface' : 'text-ink'}`}
      >
        Nox<span className={light ? 'text-primary-light' : 'text-primary'}>Verify</span>
      </span>
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ICONS -- clean line style
   ═══════════════════════════════════════════════════════════════════════════ */

const icons = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 22V4a2 2 0 012-2h8a2 2 0 012 2v18" />
      <path d="M6 12H4a2 2 0 00-2 2v6a2 2 0 002 2h2M18 9h2a2 2 0 012 2v9a2 2 0 01-2 2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  ),
  store: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9h18v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path d="M9 22V14h6v8" />
      <circle cx="7.5" cy="9" r="2.5" /><circle cx="16.5" cy="9" r="2.5" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15 15 0 014 10 15 15 0 01-4 10 15 15 0 01-4-10 15 15 0 014-10z" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
      <circle cx="12" cy="16.5" r="1.5" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
      <rect x="7" y="7" width="10" height="10" rx="1" />
      <path d="M10 12h4" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  ),
  arrowUp: (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
    </svg>
  ),
  checkCircle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  quote: (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
    </svg>
  ),
};

function Icon({ name, className = 'w-6 h-6' }: { name: keyof typeof icons; className?: string }) {
  return <span className={className} aria-hidden="true">{icons[name]}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   HOOKS
   ═══════════════════════════════════════════════════════════════════════════ */

function useActiveSection() {
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );

    for (const id of SECTION_IDS) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  return activeSection;
}

function useShowBackToTop(threshold = 400) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return show;
}

/* ═══════════════════════════════════════════════════════════════════════════
   EDITORIAL PRIMITIVES — eyebrow, numbered section header, hairline divider
   ═══════════════════════════════════════════════════════════════════════════ */

/** Mono uppercase eyebrow with a short hairline rule. Replaces gradient pills. */
function Eyebrow({
  children,
  light = false,
  className = '',
}: {
  children: React.ReactNode;
  light?: boolean;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      <span
        aria-hidden="true"
        className={`block h-px w-6 ${light ? 'bg-primary-light' : 'bg-hairline-strong'}`}
      />
      <span
        className={`font-mono text-xs uppercase tracking-[0.12em] ${light ? 'text-primary-light' : 'text-ink-muted'}`}
      >
        {children}
      </span>
    </span>
  );
}

/**
 * Numbered section header: mono section number on the left of an eyebrow
 * label, oversized IBM Plex Sans display title, optional lead. Asymmetric on lg.
 */
function SectionHeader({
  number,
  eyebrow,
  title,
  lead,
  light = false,
}: {
  number: string;
  eyebrow: string;
  title: string;
  lead?: string;
  light?: boolean;
}) {
  return (
    <div className="grid gap-x-10 gap-y-4 lg:grid-cols-12">
      <div className="lg:col-span-3">
        <div className="flex items-baseline gap-3">
          <span
            className={`font-mono text-sm ${light ? 'text-primary-light' : 'text-primary'}`}
          >
            {number}
          </span>
          <Eyebrow light={light}>{eyebrow}</Eyebrow>
        </div>
      </div>
      <div className="lg:col-span-9">
        <h2
          className={`font-display text-3xl font-medium leading-[1.05] tracking-[-0.02em] sm:text-4xl lg:text-[2.75rem] ${light ? 'text-surface' : 'text-ink'}`}
        >
          {title}
        </h2>
        {lead ? (
          <p
            className={`mt-4 max-w-2xl text-lg leading-relaxed ${light ? 'text-ink-invert' : 'text-ink-muted'}`}
          >
            {lead}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Full-bleed hairline divider between numbered sections. */
function HairlineDivider({ dark = false }: { dark?: boolean }) {
  return <div className={`h-px w-full ${dark ? 'bg-hairline-dark' : 'bg-hairline'}`} aria-hidden="true" />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   HERO ILLUSTRATION
   ═══════════════════════════════════════════════════════════════════════════ */

function HeroIllustration() {
  // "Precision Blue" recolor: white/cool-surface panels, hairline strokes, azure
  // as the single accent. Positive "CLEAR" states are azure; alert states use
  // semantic warning amber. Flat — no gradients.
  const { t } = useTranslation();
  return (
    <svg viewBox="0 0 520 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-lg mx-auto" role="img" aria-label="Illustration of identity verification workflow showing document verification, biometric matching, and screening steps">
      <rect x="60" y="30" width="400" height="300" rx="10" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
      <rect x="90" y="60" width="180" height="120" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline-strong))" strokeWidth="1.5" />
      <rect x="110" y="80" width="50" height="60" rx="6" fill="rgb(var(--c-primary-tint))" />
      <circle cx="135" cy="100" r="14" fill="rgb(var(--c-primary))" fillOpacity="0.18" />
      <path d="M128 118a7 7 0 0114 0" fill="rgb(var(--c-primary))" fillOpacity="0.18" />
      <rect x="175" y="85" width="75" height="8" rx="4" fill="rgb(var(--c-hairline-strong))" />
      <rect x="175" y="100" width="55" height="6" rx="3" fill="rgb(var(--c-hairline))" />
      <rect x="175" y="115" width="65" height="6" rx="3" fill="rgb(var(--c-hairline))" />
      <rect x="175" y="130" width="40" height="6" rx="3" fill="rgb(var(--c-hairline))" />
      <circle cx="385" cy="120" r="55" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline-strong))" strokeWidth="1.5" />
      <circle cx="385" cy="110" r="20" fill="rgb(var(--c-primary))" fillOpacity="0.16" />
      <path d="M365 145a20 20 0 0140 0" fill="rgb(var(--c-primary))" fillOpacity="0.16" />
      <circle cx="385" cy="120" r="55" fill="none" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeDasharray="6 4" opacity="0.55" />
      <path d="M275 120h40" stroke="rgb(var(--c-ink-faint))" strokeWidth="1.5" strokeDasharray="4 3" />
      <circle cx="295" cy="120" r="3" fill="rgb(var(--c-ink-faint))" />
      <g transform="translate(100, 210)">
        <rect width="90" height="90" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
        <circle cx="45" cy="36" r="16" fill="rgb(var(--c-primary-tint))" />
        <path d="M38 36l4 4 8-8" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="45" y="68" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.document')}</text>
        <text x="45" y="79" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.verified')}</text>
      </g>
      <g transform="translate(215, 210)">
        <rect width="90" height="90" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
        <circle cx="45" cy="36" r="16" fill="rgb(var(--c-primary-tint))" />
        <path d="M38 36l4 4 8-8" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="45" y="68" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.biometric')}</text>
        <text x="45" y="79" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.match')}</text>
      </g>
      <g transform="translate(330, 210)">
        <rect width="90" height="90" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
        <circle cx="45" cy="36" r="16" fill="rgb(var(--c-primary-tint))" />
        <path d="M38 36l4 4 8-8" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="45" y="68" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.screening')}</text>
        <text x="45" y="79" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.clear')}</text>
      </g>
      <circle cx="198" cy="255" r="3" fill="rgb(var(--c-ink-faint))" />
      <circle cx="325" cy="255" r="3" fill="rgb(var(--c-ink-faint))" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STEP ILLUSTRATIONS (unchanged from pre-i18n version)
   ═══════════════════════════════════════════════════════════════════════════ */

function StepIllustration({ step }: { step: string }) {
  const { t } = useTranslation();
  switch (step) {
    case '01':
      return (
        <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" role="img" aria-label="Document capture: upload identity documents and selfie">
          <rect width="400" height="280" rx="10" fill="rgb(var(--c-surface))" />
          <rect x="140" y="20" width="120" height="220" rx="10" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="2" />
          <rect x="155" y="45" width="90" height="130" rx="6" fill="rgb(var(--c-primary-tint))" />
          <circle cx="200" cy="95" r="24" fill="rgb(var(--c-primary))" fillOpacity="0.16" />
          <rect x="188" y="83" width="24" height="18" rx="4" fill="rgb(var(--c-primary))" />
          <circle cx="200" cy="92" r="5" fill="rgb(var(--c-surface-raised))" />
          <rect x="155" y="185" width="90" height="12" rx="3" fill="rgb(var(--c-hairline-strong))" />
          <rect x="155" y="200" width="60" height="8" rx="2" fill="rgb(var(--c-hairline))" />
          <path d="M120 130l-30-25M120 130l-30 25" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          <path d="M280 130l30-25M280 130l30 25" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          <circle cx="185" cy="255" r="4" fill="rgb(var(--c-primary))" />
          <circle cx="200" cy="255" r="4" fill="rgb(var(--c-hairline-strong))" />
          <circle cx="215" cy="255" r="4" fill="rgb(var(--c-hairline-strong))" />
        </svg>
      );
    case '02':
      return (
        <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" role="img" aria-label="Verification: document OCR extraction, biometric check, and sanctions screening">
          <rect width="400" height="280" rx="10" fill="rgb(var(--c-surface))" />
          <rect x="30" y="60" width="100" height="70" rx="6" fill="rgb(var(--c-primary-tint))" stroke="rgb(var(--c-hairline-strong))" strokeWidth="1.5" />
          <text x="80" y="90" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="10" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.ocr')}</text>
          <text x="80" y="105" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="9" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.extract')}</text>
          <path d="M135 95h30" stroke="rgb(var(--c-ink-faint))" strokeWidth="1.5" strokeDasharray="4 3" />
          <rect x="170" y="60" width="100" height="70" rx="6" fill="rgb(var(--c-primary-tint))" stroke="rgb(var(--c-hairline-strong))" strokeWidth="1.5" />
          <text x="220" y="90" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="10" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.biometric')}</text>
          <text x="220" y="105" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="9" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.match')}</text>
          <path d="M275 95h30" stroke="rgb(var(--c-ink-faint))" strokeWidth="1.5" strokeDasharray="4 3" />
          <rect x="310" y="60" width="70" height="70" rx="6" fill="rgb(var(--c-primary-tint))" stroke="rgb(var(--c-hairline-strong))" strokeWidth="1.5" />
          <text x="345" y="90" textAnchor="middle" fill="rgb(var(--c-ink))" fontSize="10" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.screen')}</text>
          <text x="345" y="105" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="9" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.pepSan')}</text>
          <rect x="60" y="155" width="280" height="90" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1.5" />
          <rect x="80" y="170" width="60" height="8" rx="3" fill="rgb(var(--c-hairline-strong))" />
          <rect x="80" y="185" width="200" height="6" rx="2" fill="rgb(var(--c-hairline))" />
          <rect x="80" y="198" width="160" height="6" rx="2" fill="rgb(var(--c-hairline))" />
          <rect x="80" y="211" width="180" height="6" rx="2" fill="rgb(var(--c-hairline))" />
          <circle cx="310" cy="195" r="16" fill="rgb(var(--c-primary-tint))" />
          <path d="M303 195l4 4 8-8" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case '03':
      return (
        <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" role="img" aria-label="Decision: automated approval or manual review queue">
          <rect width="400" height="280" rx="10" fill="rgb(var(--c-surface))" />
          <rect x="40" y="40" width="150" height="100" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-primary))" strokeOpacity="0.4" strokeWidth="2" />
          <text x="115" y="68" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="11" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.autoApproved')}</text>
          <circle cx="80" cy="100" r="14" fill="rgb(var(--c-primary-tint))" />
          <path d="M73 100l4 4 8-8" stroke="rgb(var(--c-primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <text x="115" y="105" textAnchor="middle" fill="rgb(var(--c-primary))" fontSize="9" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.lowRisk')}</text>
          <text x="115" y="120" textAnchor="middle" fill="rgb(var(--c-ink-muted))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.score')}</text>
          <rect x="210" y="40" width="150" height="100" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-warning))" strokeOpacity="0.5" strokeWidth="2" />
          <text x="285" y="68" textAnchor="middle" fill="rgb(var(--c-warning))" fontSize="11" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.manualReview')}</text>
          <circle cx="250" cy="100" r="14" fill="rgb(var(--c-warning-tint))" />
          <text x="250" y="104" textAnchor="middle" fill="rgb(var(--c-warning))" fontSize="14" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">!</text>
          <text x="285" y="105" textAnchor="middle" fill="rgb(var(--c-warning))" fontSize="9" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.needsAnalyst')}</text>
          <text x="285" y="120" textAnchor="middle" fill="rgb(var(--c-ink-muted))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.priorityHigh')}</text>
          <rect x="60" y="165" width="280" height="90" rx="8" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1.5" />
          <text x="200" y="185" textAnchor="middle" fill="rgb(var(--c-ink-muted))" fontSize="10" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.reviewQueue')}</text>
          <rect x="80" y="195" width="240" height="16" rx="3" fill="rgb(var(--c-surface-sunken))" />
          <rect x="80" y="195" width="170" height="16" rx="3" fill="rgb(var(--c-primary-tint))" />
          <rect x="80" y="218" width="240" height="16" rx="3" fill="rgb(var(--c-surface-sunken))" />
          <rect x="80" y="218" width="100" height="16" rx="3" fill="rgb(var(--c-warning-tint))" />
        </svg>
      );
    case '04':
      return (
        <svg viewBox="0 0 400 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" role="img" aria-label="Ongoing monitoring: continuous rescreening and risk detection">
          <rect width="400" height="280" rx="10" fill="rgb(var(--c-surface))" />
          <line x1="60" y1="60" x2="60" y2="230" stroke="rgb(var(--c-hairline))" strokeWidth="2" />
          <circle cx="60" cy="80" r="8" fill="rgb(var(--c-primary))" />
          <rect x="80" y="68" width="260" height="28" rx="6" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
          <text x="95" y="86" fill="rgb(var(--c-ink-muted))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.initialScreening')}</text>
          <text x="300" y="86" textAnchor="end" fill="rgb(var(--c-ink-faint))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.day1')}</text>
          <circle cx="60" cy="120" r="8" fill="rgb(var(--c-primary))" />
          <rect x="80" y="108" width="260" height="28" rx="6" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
          <text x="95" y="126" fill="rgb(var(--c-ink-muted))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.rescreen30')}</text>
          <text x="300" y="126" textAnchor="end" fill="rgb(var(--c-ink-faint))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.day30')}</text>
          <circle cx="60" cy="160" r="8" fill="rgb(var(--c-primary))" />
          <rect x="80" y="148" width="260" height="28" rx="6" fill="rgb(var(--c-surface-raised))" stroke="rgb(var(--c-hairline))" strokeWidth="1" />
          <text x="95" y="166" fill="rgb(var(--c-ink-muted))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.rescreen60')}</text>
          <text x="300" y="166" textAnchor="end" fill="rgb(var(--c-ink-faint))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.day60')}</text>
          <circle cx="60" cy="200" r="8" fill="rgb(var(--c-warning))" />
          <rect x="80" y="188" width="260" height="28" rx="6" fill="rgb(var(--c-warning-tint))" stroke="rgb(var(--c-warning))" strokeOpacity="0.4" strokeWidth="1" />
          <text x="95" y="206" fill="rgb(var(--c-warning))" fontSize="9" fontWeight="600" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.pepAlert')}</text>
          <text x="300" y="206" textAnchor="end" fill="rgb(var(--c-ink-faint))" fontSize="8" fontFamily="'IBM Plex Mono', monospace">{t('marketing:illustrations.day91')}</text>
          <circle cx="60" cy="230" r="4" fill="rgb(var(--c-hairline))" />
          <circle cx="60" cy="242" r="3" fill="rgb(var(--c-hairline))" />
          <circle cx="60" cy="252" r="2" fill="rgb(var(--c-hairline))" />
        </svg>
      );
    default:
      return null;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   DATA (icons/colors only — copy lives in i18n marketing namespace)
   ═══════════════════════════════════════════════════════════════════════════ */

const featuresStructure = [
  { key: 'kyc', icon: 'shield' as const },
  { key: 'kyb', icon: 'building' as const },
  { key: 'kym', icon: 'store' as const },
  // The remaining 3 features in the current English copy are jurisdiction,
  // access control, audit. They have copy in the legacy ja layout but the
  // canonical i18n schema only exposes kyc/kyb/kym/screening/decisioning/audit.
  { key: 'screening', icon: 'globe' as const },
  { key: 'decisioning', icon: 'lock' as const },
  { key: 'audit', icon: 'clipboard' as const },
];

const statsStructure = [
  { key: 'accuracy', sublabelKey: null },
  { key: 'speed', sublabelKey: null },
  { key: 'countries', sublabelKey: null },
  { key: 'uptime', sublabelKey: null },
];

const howItWorksStructure = [
  { step: '01', key: 'step1', icon: 'scan' as const },
  { step: '02', key: 'step2', icon: 'eye' as const },
  { step: '03', key: 'step3', icon: 'check' as const },
  { step: '04', key: 'step4', icon: 'zap' as const },
];

// Structural-only data (icons, highlight flags). All user-visible strings live
// in `packages/i18n/locales/en/marketing.json` under the matching keys and are
// resolved through `t(...)` at render time.
const complianceBadgesStructure = [
  { key: 0, icon: 'lock' as const },
  { key: 1, icon: 'lock' as const },
  { key: 2, icon: 'shield' as const },
];

const pricingTiersStructure = [
  { key: 'starter', highlighted: false },
  { key: 'business', highlighted: true },
  { key: 'enterprise', highlighted: false },
] as const;

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

function scrollToDemo(e?: React.MouseEvent) {
  if (e) e.preventDefault();
  const el = document.getElementById('demo');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

/* ═══════════════════════════════════════════════════════════════════════════
   SECTIONS
   ═══════════════════════════════════════════════════════════════════════════ */

function SkipToContent() {
  const { t } = useTranslation();
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-on-primary focus:rounded-md focus:text-sm focus:font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      {t('common:common.skipToContent')}
    </a>
  );
}

function Navigation({ activeSection }: { activeSection: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const localize = useLocalizedPath();
  const homePath = localize('/');
  const isLanding = location.pathname === homePath;

  const links = [
    { href: '#features', label: t('common:nav.features'), id: 'features' },
    { href: '#how-it-works', label: t('common:nav.howItWorks'), id: 'how-it-works' },
    { href: '#compliance', label: t('common:nav.compliance'), id: 'compliance' },
    { href: '#pricing', label: t('common:nav.pricing'), id: 'pricing' },
  ];

  const getSectionHref = (hash: string) => (isLanding ? hash : `${homePath}${hash}`);
  const getDemoHref = () => (isLanding ? '#demo' : `${homePath}#demo`);

  const handleDemoClick = (e: React.MouseEvent) => {
    if (isLanding) {
      scrollToDemo(e);
    }
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-surface/85 backdrop-blur-xl border-b border-hairline" aria-label="Main">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={getSectionHref(l.href)}
              className={`rounded-sm px-1 py-0.5 text-sm font-medium transition-colors ${FOCUS_VISIBLE_RING} ${
                isLanding && activeSection === l.id ? 'text-primary' : 'text-ink-muted hover:text-ink'
              }`}
              {...(isLanding && activeSection === l.id ? { 'aria-current': 'true' as const } : {})}
            >
              {l.label}
            </a>
          ))}
          <span className="w-px h-5 bg-hairline-strong" aria-hidden="true" />
          {/* Preferences toolbar: language + theme, grouped so both are
              prominent and adjacent before the auth CTAs. */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
          <a href={SIGN_IN_URL} className={`rounded-sm px-1 py-0.5 text-sm font-medium text-ink-muted hover:text-ink ${FOCUS_VISIBLE_RING}`} rel="noopener noreferrer">
            {t('common:cta.signIn')}
          </a>
          <a
            href={getDemoHref()}
            onClick={handleDemoClick}
            className={`px-4 py-2 bg-primary text-on-primary rounded-md text-sm font-semibold transition-colors duration-150 hover:bg-primary-hover ${FOCUS_VISIBLE_RING}`}
          >
            {t('common:cta.requestDemo')}
          </a>
        </div>
        <button
          className={`md:hidden p-2 rounded-md text-ink hover:bg-surface-sunken ${FOCUS_VISIBLE_RING}`}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? t('common:common.closeMenu') : t('common:common.openMenu')}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            {open ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-hairline bg-surface py-4 px-4 animate-fade-in" role="menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={getSectionHref(l.href)}
              onClick={() => setOpen(false)}
              className={`block px-3 py-2.5 text-sm font-medium rounded-md ${FOCUS_VISIBLE_RING} ${
                isLanding && activeSection === l.id ? 'text-primary bg-primary-tint' : 'text-ink-muted hover:bg-surface-sunken'
              }`}
              role="menuitem"
              {...(isLanding && activeSection === l.id ? { 'aria-current': 'true' as const } : {})}
            >
              {l.label}
            </a>
          ))}
          <hr className="my-3 border-hairline" />
          <div className="px-3 py-2 flex flex-wrap items-center gap-3">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
          <a href={SIGN_IN_URL} className={`block px-3 py-2.5 text-sm font-medium text-ink-muted rounded-md hover:bg-surface-sunken ${FOCUS_VISIBLE_RING}`} rel="noopener noreferrer" role="menuitem">
            {t('common:cta.signIn')}
          </a>
          <a
            href={getDemoHref()}
            onClick={(e) => {
              if (isLanding) scrollToDemo(e);
              setOpen(false);
            }}
            className={`block mt-2 px-3 py-2.5 bg-primary text-on-primary rounded-md text-sm font-semibold text-center transition-colors hover:bg-primary-hover ${FOCUS_VISIBLE_RING}`}
            role="menuitem"
          >
            {t('common:cta.requestDemo')}
          </a>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="pt-32 pb-16 sm:pt-40 sm:pb-24 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-x-10 gap-y-12 items-center">
          {/* Above-the-fold LCP block: NO entrance animation. animate-slide-up
              starts at opacity:0, which delayed the largest-contentful-paint
              (the <h1>) until JS resolved the animation. The SSR-prerendered
              hero must paint at full opacity on first paint. */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <Eyebrow>{t('marketing:hero.eyebrow')}</Eyebrow>
              <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
            </div>
            <h1 className="font-display font-medium text-ink leading-[0.98] tracking-[-0.025em] text-[clamp(2.75rem,6vw,5.25rem)] mb-6">
              {t('marketing:hero.title')}
            </h1>
            <p className="text-lg text-ink-muted mb-9 leading-relaxed max-w-xl">
              {t('marketing:hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#demo"
                onClick={scrollToDemo}
                className={`group inline-flex items-center justify-center px-7 py-3.5 bg-primary text-on-primary rounded-md font-semibold transition-colors duration-150 hover:bg-primary-hover ${FOCUS_VISIBLE_RING}`}
              >
                {t('marketing:hero.primaryCta')}{' '}
                <Icon name="arrowRight" className="w-4 h-4 ms-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#how-it-works" className={`inline-flex items-center justify-center px-7 py-3.5 bg-transparent text-ink rounded-md font-semibold border border-hairline-strong hover:border-ink transition-colors ${FOCUS_VISIBLE_RING}`}>
                {t('marketing:hero.secondaryCta')}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-xs uppercase tracking-[0.08em] text-ink-muted">
              <span>185 {t('marketing:stats.countries.label')}</span>
              <span aria-hidden="true" className="text-hairline-strong">·</span>
              <span>{t('marketing:stats.uptime.value')} {t('marketing:stats.uptime.label')}</span>
              <span aria-hidden="true" className="text-hairline-strong">·</span>
              <span>{t('marketing:stats.speed.value')} {t('marketing:stats.speed.label')}</span>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <HeroIllustration />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustedBy() {
  const { t } = useTranslation();
  const items = t('marketing:trustedBy.items', { returnObjects: true }) as unknown;
  const list = Array.isArray(items) ? (items as string[]) : [];
  return (
    <section className="py-10 border-y border-hairline" aria-label="Regulatory framework support">
      <div className="max-w-7xl mx-auto px-4">
        <p className="text-center font-mono text-xs uppercase tracking-[0.12em] text-ink-muted mb-6">
          {t('marketing:trustedBy.eyebrow')}
        </p>
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 sm:gap-x-14">
          {list.map((tx) => (
            <span key={tx} className="font-mono text-sm uppercase tracking-wide text-ink-muted">{tx}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComplianceBadges() {
  const { t } = useTranslation();
  const items = t('marketing:complianceBadges.items', { returnObjects: true }) as unknown;
  const labels = Array.isArray(items) ? (items as string[]) : [];
  return (
    <section className="py-12 bg-surface-sunken/60" aria-label="Compliance and security certifications">
      <div className="max-w-5xl mx-auto px-4">
        <p className="text-center font-mono text-xs uppercase tracking-[0.12em] text-ink-muted mb-8">
          {t('marketing:complianceBadges.eyebrow')}
        </p>
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
          {complianceBadgesStructure.map((badge, idx) => {
            const label = labels[idx] ?? '';
            return (
              <div key={label || badge.key} className="inline-flex items-center gap-2.5 px-5 py-3 rounded-md bg-surface-raised border border-hairline">
                <Icon name={badge.icon} className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-ink">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const { t } = useTranslation();
  return (
    <section className="py-16 bg-surface-raised border-b border-hairline" aria-label="Platform statistics">
      <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-px bg-hairline border border-hairline rounded-lg overflow-hidden">
        {statsStructure.map((s) => (
          <div key={s.key} className="bg-surface-raised px-6 py-8 text-center">
            <div className="font-display text-4xl sm:text-5xl font-medium text-ink tracking-[-0.02em]">
              {t(`marketing:stats.${s.key}.value`)}
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.1em] text-ink-muted mt-3">
              {t(`marketing:stats.${s.key}.label`)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  const { t } = useTranslation();
  return (
    <section id="features" className="py-24 sm:py-28 px-4 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          number="01"
          eyebrow={t('common:nav.features')}
          title={t('marketing:features.title')}
          lead={t('marketing:features.subtitle')}
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 border-t border-s border-hairline">
          {featuresStructure.map((f) => (
            <div
              key={f.key}
              className="group border-b border-e border-hairline p-8 transition-colors hover:bg-surface-raised"
            >
              <span className="inline-flex w-10 h-10 items-center justify-center text-primary" aria-hidden="true">
                <Icon name={f.icon} className="w-7 h-7" />
              </span>
              <h3 className="font-display text-xl font-medium text-ink mt-5 mb-2 tracking-[-0.01em]">
                {t(`marketing:features.${f.key}.title`)}
              </h3>
              <p className="text-sm text-ink-muted leading-relaxed">
                {t(`marketing:features.${f.key}.description`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="how-it-works" className="py-24 sm:py-28 px-4 bg-surface-sunken/50 border-y border-hairline scroll-mt-20">
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          number="02"
          eyebrow={t('common:nav.howItWorks')}
          title={t('marketing:howItWorks.title')}
          lead={t('marketing:howItWorks.subtitle')}
        />
        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2 flex flex-col" role="tablist" aria-label="Verification steps">
            {howItWorksStructure.map((s, idx) => (
              <button
                key={s.step}
                role="tab"
                aria-selected={activeStep === idx}
                aria-controls={`step-panel-${s.step}`}
                id={`step-tab-${s.step}`}
                onClick={() => setActiveStep(idx)}
                className={`text-start p-5 border-s-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
                  activeStep === idx
                    ? 'border-s-primary bg-surface-raised'
                    : 'border-s-hairline hover:bg-surface-raised/60'
                }`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`flex-shrink-0 font-mono text-sm pt-0.5 transition-colors ${activeStep === idx ? 'text-primary' : 'text-ink-muted'}`}
                  >
                    {s.step}
                  </span>
                  <div>
                    <h3 className={`font-display text-lg font-medium transition-colors ${activeStep === idx ? 'text-ink' : 'text-ink-muted'}`}>
                      {t(`marketing:howItWorks.${s.key}.title`)}
                    </h3>
                    {activeStep === idx && (
                      <p className="text-sm text-ink-muted leading-relaxed mt-2 animate-fade-in">
                        {t(`marketing:howItWorks.${s.key}.description`)}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            {howItWorksStructure.map((s, idx) => (
              <div
                key={s.step}
                role="tabpanel"
                id={`step-panel-${s.step}`}
                aria-labelledby={`step-tab-${s.step}`}
                hidden={activeStep !== idx}
                className={activeStep === idx ? 'animate-fade-in' : ''}
              >
                <div className="bg-surface-raised rounded-lg border border-hairline p-6">
                  <StepIllustration step={s.step} />
                  <div className="mt-4 lg:hidden">
                    <p className="text-sm text-ink-muted leading-relaxed">{t(`marketing:howItWorks.${s.key}.description`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Compliance() {
  const { t } = useTranslation();
  const items = t('marketing:compliance.items', { returnObjects: true }) as unknown;
  const list = Array.isArray(items) ? (items as string[]) : [];
  return (
    <section id="compliance" className="py-24 sm:py-28 px-4 bg-ink-surface scroll-mt-20">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          number="03"
          eyebrow={t('common:nav.compliance')}
          title={t('marketing:compliance.title')}
          lead={t('marketing:compliance.subtitle')}
          light
        />
        <div className="mt-14 grid md:grid-cols-2 border-t border-s border-hairline-dark">
          {list.map((f) => (
            <div key={f} className="flex items-start gap-3 p-5 border-b border-e border-hairline-dark transition-colors hover:bg-ink-surface2">
              <svg className="w-5 h-5 text-primary-light mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-ink-invert leading-relaxed">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IntegrationSection() {
  const { t } = useTranslation();
  return (
    <section className="py-24 sm:py-28 px-4 bg-ink-surface">
      <div className="max-w-5xl mx-auto">
        <div className="mb-12">
          <SectionHeader
            number="04"
            eyebrow={t('marketing:footer.links.apiDocs')}
            title={t('marketing:integration.title')}
            lead={t('marketing:integration.subtitle')}
            light
          />
        </div>
        <div className="bg-ink-surface2 rounded-lg border border-hairline-dark overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-hairline-dark">
            <span className="w-2.5 h-2.5 rounded-full bg-ink-invert/20" aria-hidden="true" />
            <span className="w-2.5 h-2.5 rounded-full bg-ink-invert/20" aria-hidden="true" />
            <span className="w-2.5 h-2.5 rounded-full bg-ink-invert/20" aria-hidden="true" />
            <span className="ms-3 text-xs text-footer-muted font-mono">verify.ts</span>
          </div>
          <pre className="p-6 sm:p-8 text-sm leading-relaxed overflow-x-auto font-mono" aria-label="Code example showing how to integrate NoxVerify SDK">
            <code>
              <span className="text-primary-light">import</span>{' '}<span className="text-footer-muted">{'{ '}</span><span className="text-ink-invert">createNoxVerifyClient</span><span className="text-footer-muted">{' }'}</span>{' '}<span className="text-primary-light">from</span>{' '}<span className="text-ink-invert">{`'@noxverify/sdk'`}</span><span className="text-footer-muted">;</span>{'\n\n'}<span className="text-primary-light">const</span>{' '}<span className="text-ink-invert">nox</span>{' '}<span className="text-footer-muted">=</span>{' '}<span className="text-ink-invert">createNoxVerifyClient</span><span className="text-footer-muted">{'({ '}</span><span className="text-ink-invert">apiKey</span><span className="text-footer-muted">:</span>{' '}<span className="text-ink-invert">{`'nv_live_...'`}</span><span className="text-footer-muted">{' })'}</span><span className="text-footer-muted">;</span>{'\n\n'}<span className="text-primary-light">const</span>{' '}<span className="text-ink-invert">verification</span>{' '}<span className="text-footer-muted">=</span>{' '}<span className="text-primary-light">await</span>{' '}<span className="text-ink-invert">nox</span><span className="text-footer-muted">.</span><span className="text-ink-invert">verifyIdentity</span><span className="text-footer-muted">{'({'}</span>{'\n'}<span className="text-footer-muted">{'  '}</span><span className="text-ink-invert">type</span><span className="text-footer-muted">:</span>{' '}<span className="text-ink-invert">{`'kyc_individual'`}</span><span className="text-footer-muted">,</span>{'\n'}<span className="text-footer-muted">{'  '}</span><span className="text-ink-invert">returnUrl</span><span className="text-footer-muted">:</span>{' '}<span className="text-ink-invert">{`'https://yourapp.com/callback'`}</span><span className="text-footer-muted">,</span>{'\n'}<span className="text-footer-muted">{'}'}</span><span className="text-footer-muted">{')'}</span><span className="text-footer-muted">;</span>
            </code>
          </pre>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
          <a href="#demo" onClick={scrollToDemo} className={`group inline-flex items-center justify-center px-6 py-3 bg-surface text-ink rounded-md font-semibold hover:bg-surface-raised transition-colors text-sm ${FOCUS_VISIBLE_RING}`}>
            {t('marketing:integration.ctaPrimary')} <Icon name="arrowRight" className="w-4 h-4 ms-2 group-hover:translate-x-1 transition-transform" />
          </a>
          <span className="text-sm text-footer-muted">{t('marketing:integration.ctaSubtext')}</span>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const items = t('marketing:faq.items', { returnObjects: true }) as unknown;
  const faqs: { q: string; a: string }[] = Array.isArray(items) ? (items as { q: string; a: string }[]) : [];

  const toggleFAQ = (idx: number) => setOpenIndex((prev) => (prev === idx ? null : idx));

  return (
    <section className="py-24 sm:py-28 px-4" aria-label="Frequently asked questions">
      <div className="max-w-3xl mx-auto">
        <div className="mb-12">
          <Eyebrow>{t('marketing:faq.subtitle')}</Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl font-medium text-ink mt-4 tracking-[-0.02em]">{t('marketing:faq.title')}</h2>
        </div>
        <div className="border-t border-hairline" role="list">
          {faqs.map((faq, idx) => (
            <div key={faq.q} className="border-b border-hairline" role="listitem">
              <button
                onClick={() => toggleFAQ(idx)}
                className="w-full flex items-center justify-between gap-4 py-5 text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
                aria-expanded={openIndex === idx}
                aria-controls={`faq-answer-${idx}`}
                id={`faq-question-${idx}`}
              >
                <span className="font-display text-lg font-medium text-ink">{faq.q}</span>
                <span className={`w-5 h-5 flex-shrink-0 text-primary transition-transform duration-200 ${openIndex === idx ? 'rotate-180' : ''}`}>
                  <Icon name="chevronDown" className="w-5 h-5" />
                </span>
              </button>
              <div
                id={`faq-answer-${idx}`}
                role="region"
                aria-labelledby={`faq-question-${idx}`}
                className={`overflow-hidden transition-all duration-200 ${openIndex === idx ? 'max-h-96' : 'max-h-0'}`}
              >
                <p className="pb-5 text-sm text-ink-muted leading-relaxed max-w-2xl">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { t } = useTranslation();
  return (
    <section id="pricing" className="py-24 sm:py-28 px-4 bg-surface-sunken/50 border-y border-hairline scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          number="05"
          eyebrow={t('common:nav.pricing')}
          title={t('marketing:pricing.title')}
          lead={t('marketing:pricing.subtitle')}
        />
        <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricingTiersStructure.map((tier) => {
            const features = t(`marketing:pricing.tiers.${tier.key}.features`, { returnObjects: true }) as unknown;
            const featureList: string[] = Array.isArray(features) ? (features as string[]) : [];
            const name = t(`marketing:pricing.tiers.${tier.key}.name`);
            const price = t(`marketing:pricing.tiers.${tier.key}.price`);
            const description = t(`marketing:pricing.tiers.${tier.key}.description`);
            const cta = t(`marketing:pricing.tiers.${tier.key}.cta`);
            const isContactSales = tier.key === 'enterprise';
            return (
              <div
                key={tier.key}
                className={`relative flex flex-col rounded-lg bg-surface-raised p-8 transition-colors ${
                  tier.highlighted
                    ? 'border border-primary ring-1 ring-primary/30'
                    : 'border border-hairline hover:border-hairline-strong'
                }`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-2.5 start-8 inline-flex items-center bg-surface-raised px-2 font-mono text-[0.65rem] uppercase tracking-[0.14em] text-primary">
                    {t('marketing:pricing.mostPopular')}
                  </span>
                )}
                <h3 className="font-display text-xl font-medium text-ink mb-1">{name}</h3>
                <div className="font-display text-3xl font-medium text-ink mb-3 tracking-[-0.02em]">
                  {price}
                </div>
                <p className="text-sm text-ink-muted mb-6">{description}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {featureList.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-ink-muted">{f}</span>
                    </li>
                  ))}
                </ul>
                {isContactSales ? (
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=Enterprise%20inquiry`}
                    className={`block w-full py-3 rounded-md font-semibold text-sm text-center transition-colors duration-150 ${FOCUS_VISIBLE_RING} ${
                      tier.highlighted
                        ? 'bg-primary text-on-primary hover:bg-primary-hover'
                        : 'border border-hairline-strong text-ink hover:border-ink'
                    }`}
                  >
                    {cta}
                  </a>
                ) : (
                  <a
                    href="#demo"
                    onClick={scrollToDemo}
                    className={`block w-full py-3 rounded-md font-semibold text-sm text-center transition-colors duration-150 ${FOCUS_VISIBLE_RING} ${
                      tier.highlighted
                        ? 'bg-primary text-on-primary hover:bg-primary-hover'
                        : 'border border-hairline-strong text-ink hover:border-ink'
                    }`}
                  >
                    {cta}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DEMO REQUEST FORM
   ═══════════════════════════════════════════════════════════════════════════ */

function DemoRequestForm() {
  const { t } = useTranslation();
  const localize = useLocalizedPath();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!consent) {
        setError(t('marketing:demoForm.errors.consentRequired'));
        return;
      }
      setSubmitting(true);
      setError('');

      try {
        const apiBase = import.meta.env.VITE_API_BASE_URL || '';
        const res = await fetch(`${apiBase}/v1/public/leads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (res.ok) {
          setSubmitted(true);
        } else {
          const json = (await res.json()) as { error?: { message?: string } };
          setError(json.error?.message ?? t('marketing:demoForm.errors.generic'));
        }
      } catch {
        setError(t('marketing:demoForm.errors.network'));
      } finally {
        setSubmitting(false);
      }
    },
    [formData, consent, t],
  );

  if (submitted) {
    return (
      <div className="text-center py-12" role="status" aria-live="polite">
        <div className="w-16 h-16 rounded-full bg-primary-tint flex items-center justify-center mx-auto mb-6">
          <Icon name="checkCircle" className="w-8 h-8 text-primary" />
        </div>
        <h3 className="font-display text-2xl font-medium text-ink mb-2">{t('marketing:demoForm.successHeading')}</h3>
        <p className="text-ink-muted max-w-md mx-auto">
          <Trans
            i18nKey="marketing:demoForm.successBody"
            values={{ email: formData.email }}
            components={[<span key="email" className="font-mono text-ink" />]}
          />
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto" aria-describedby="demo-form-note">
      {error && (
        <div className="sm:col-span-2 rounded-md bg-danger-tint border border-danger/30 px-4 py-3 text-sm text-danger" role="alert">
          {error}
        </div>
      )}
      <div className="sm:col-span-1">
        <label htmlFor="demo-name" className="block text-sm font-medium text-ink mb-1.5">{t('marketing:demoForm.nameLabel')} <span className="text-danger" aria-hidden="true">*</span></label>
        <input id="demo-name" name="name" type="text" required autoComplete="name" value={formData.name} onChange={handleChange} disabled={submitting} className="w-full px-4 py-3 rounded-md border border-hairline-strong bg-white text-ink text-sm placeholder-ink-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors disabled:opacity-50" placeholder={t('marketing:demoForm.namePlaceholder')} />
      </div>
      <div className="sm:col-span-1">
        <label htmlFor="demo-email" className="block text-sm font-medium text-ink mb-1.5">{t('marketing:demoForm.emailLabel')} <span className="text-danger" aria-hidden="true">*</span></label>
        <input id="demo-email" name="email" type="email" required autoComplete="email" value={formData.email} onChange={handleChange} disabled={submitting} className="w-full px-4 py-3 rounded-md border border-hairline-strong bg-white text-ink text-sm placeholder-ink-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors disabled:opacity-50" placeholder={t('marketing:demoForm.emailPlaceholder')} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="demo-company" className="block text-sm font-medium text-ink mb-1.5">{t('marketing:demoForm.companyLabel')}</label>
        <input id="demo-company" name="company" type="text" autoComplete="organization" value={formData.company} onChange={handleChange} disabled={submitting} className="w-full px-4 py-3 rounded-md border border-hairline-strong bg-white text-ink text-sm placeholder-ink-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors disabled:opacity-50" placeholder={t('marketing:demoForm.companyPlaceholder')} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="demo-message" className="block text-sm font-medium text-ink mb-1.5">{t('marketing:demoForm.messageLabel')}</label>
        <textarea id="demo-message" name="message" rows={4} value={formData.message} onChange={handleChange} disabled={submitting} className="w-full px-4 py-3 rounded-md border border-hairline-strong bg-white text-ink text-sm placeholder-ink-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y disabled:opacity-50" placeholder={t('marketing:demoForm.messagePlaceholder')} />
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input id="demo-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={submitting} required aria-describedby="demo-consent-help" className="mt-1 h-4 w-4 rounded-sm border-hairline-strong text-primary focus:ring-primary" />
          <span className="text-sm text-ink-muted">
            <Trans
              i18nKey="marketing:demoForm.consentLabel"
              components={[
                <Link key="privacy" to={localize('/privacy')} className="text-primary hover:text-primary-hover underline" />,
              ]}
            />
          </span>
        </label>
        <p id="demo-consent-help" className="mt-2 text-xs text-ink-muted">
          {t('marketing:demoForm.consentHelp')}
        </p>
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={submitting || !consent} aria-describedby="demo-form-note demo-consent-help" className={`w-full sm:w-auto px-8 py-3.5 bg-primary text-on-primary rounded-md font-semibold hover:bg-primary-hover transition-colors duration-150 text-sm disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_VISIBLE_RING}`}>
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('common:common.loading')}
            </span>
          ) : (
            t('common:cta.requestDemo')
          )}
        </button>
        <p id="demo-form-note" className="text-xs text-ink-muted mt-3">{t('marketing:demoForm.formNote')}</p>
      </div>
    </form>
  );
}

function DemoSection() {
  const { t } = useTranslation();
  return (
    <section id="demo" className="py-24 sm:py-28 px-4 bg-surface-sunken/50 border-t border-hairline scroll-mt-20">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex"><LogoMark size={48} /></div>
          <h2 className="font-display text-3xl sm:text-4xl font-medium text-ink mt-6 mb-4 tracking-[-0.02em]">{t('marketing:finalCta.title')}</h2>
          <p className="text-lg text-ink-muted max-w-xl mx-auto">{t('marketing:finalCta.subtitle')}</p>
        </div>
        <DemoRequestForm />
      </div>
    </section>
  );
}

function CTA() {
  const { t } = useTranslation();
  return (
    <section className="py-24 sm:py-28 px-4">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-medium text-ink mb-4 tracking-[-0.02em]">{t('marketing:ctaBanner.title')}</h2>
        <p className="text-lg text-ink-muted mb-10">{t('marketing:ctaBanner.subtitle')}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#demo" onClick={scrollToDemo} className={`group inline-flex items-center justify-center px-8 py-3.5 bg-primary text-on-primary rounded-md font-semibold hover:bg-primary-hover transition-colors duration-150 ${FOCUS_VISIBLE_RING}`}>
            {t('common:cta.requestDemo')} <Icon name="arrowRight" className="w-4 h-4 ms-2 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className={`inline-flex items-center justify-center px-8 py-3.5 bg-transparent text-ink rounded-md font-semibold border border-hairline-strong hover:border-ink transition-colors ${FOCUS_VISIBLE_RING}`}>{t('marketing:ctaBanner.contactSales')}</a>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════════════════════════════ */

function FooterLink({ label, to }: { label: string; to: string }) {
  const localize = useLocalizedPath();
  // Hash-only fragments stay as-is; hash-with-path get localized
  if (to.startsWith('/#')) {
    const path = to.slice(1); // '#anchor'
    return (
      <a href={`${localize('/')}${path}`} className="text-sm text-footer-muted hover:text-primary-light transition-colors">
        {label}
      </a>
    );
  }
  return (
    <Link to={localize(to)} className="text-sm text-footer-muted hover:text-primary-light transition-colors">
      {label}
    </Link>
  );
}

function Footer() {
  const { t } = useTranslation();

  const cols = [
    {
      title: t('marketing:footer.columns.product'),
      links: [
        { label: t('common:nav.features'), to: '/#features' },
        { label: t('common:nav.howItWorks'), to: '/#how-it-works' },
        { label: t('common:nav.pricing'), to: '/#pricing' },
        { label: t('marketing:footer.links.apiDocs'), to: '/api-docs' },
        { label: t('marketing:footer.links.changelog'), to: '/changelog' },
      ],
    },
    {
      title: t('marketing:footer.columns.company'),
      links: [
        { label: t('marketing:footer.links.about'), to: '/about' },
        { label: t('common:nav.blog'), to: '/blog' },
        { label: t('marketing:footer.links.careers'), to: '/careers' },
        { label: t('common:nav.contact'), to: '/contact' },
      ],
    },
    {
      title: t('marketing:footer.columns.legal'),
      links: [
        { label: t('common:footer.privacyPolicy'), to: '/privacy' },
        { label: t('common:footer.termsOfService'), to: '/terms' },
        { label: t('marketing:footer.links.security'), to: '/security' },
        { label: t('marketing:footer.links.dpa'), to: '/dpa' },
        { label: t('marketing:footer.links.cookies'), to: '/cookies' },
        { label: t('marketing:footer.links.aup'), to: '/aup' },
        { label: t('marketing:footer.links.sla'), to: '/sla' },
      ],
    },
    {
      title: t('marketing:footer.columns.compliance'),
      links: [
        { label: t('common:nav.compliance'), to: '/compliance' },
        { label: t('marketing:footer.links.status'), to: '/status' },
      ],
    },
  ];

  return (
    <footer className="bg-ink-surface text-ink-invert pt-16 pb-10 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 md:grid-cols-6 gap-10 mb-14">
          <div className="sm:col-span-2">
            <Logo light />
            <p className="text-sm text-footer-muted mt-4 leading-relaxed max-w-xs">
              {t('marketing:hero.subtitle')}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <LanguageSwitcher variant="light" />
              <ThemeSwitcher variant="light" />
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              {/* Link-group labels are non-heading <p> (mono eyebrow style) to
                  avoid a skipped heading level after section <h2>s — see
                  design-system/MASTER.md §6. */}
              <p className="font-mono text-xs uppercase tracking-[0.12em] text-footer-muted mb-4">{col.title}</p>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <FooterLink label={l.label} to={l.to} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-hairline-dark pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-mono text-xs text-footer-muted">
            &copy; {new Date().getFullYear()} NoxVerify. {t('common:footer.allRightsReserved')}
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.08em] text-footer-muted">{t('marketing:footer.tagline')}</div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BACK TO TOP & SCROLL RESET
   ═══════════════════════════════════════════════════════════════════════════ */

function BackToTop() {
  const { t } = useTranslation();
  const show = useShowBackToTop(400);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label={t('common:common.backToTop')}
      className={`fixed bottom-6 end-6 z-50 w-11 h-11 rounded-md bg-surface-raised border border-hairline-strong flex items-center justify-center text-ink-muted hover:text-primary hover:border-primary transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <Icon name="arrowUp" className="w-5 h-5" />
    </button>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   LANDING PAGE -- composes the marketing sections + page SEO
   ═══════════════════════════════════════════════════════════════════════════ */

function LandingPage() {
  const { t } = useTranslation();
  const items = t('marketing:faq.items', { returnObjects: true }) as unknown;
  const faqs: { q: string; a: string }[] = Array.isArray(items) ? (items as { q: string; a: string }[]) : [];

  return (
    <>
      <SEO
        titleKey="seo:home.title"
        descriptionKey="seo:home.description"
        keywordsKey="seo:home.keywords"
        path="/"
        jsonLd={[
          organizationLd,
          websiteLd,
          softwareApplicationLd,
          kycServiceLd,
          kybServiceLd,
          kymServiceLd,
          ...(faqs.length > 0 ? [faqLd(faqs)] : []),
        ]}
      />
      <Hero />
      <TrustedBy />
      <ComplianceBadges />
      <Stats />
      <Features />
      <HowItWorks />
      <Compliance />
      <IntegrationSection />
      <HairlineDivider />
      <FAQSection />
      <Pricing />
      <DemoSection />
      <CTA />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   NOT FOUND
   ═══════════════════════════════════════════════════════════════════════════ */

function NotFoundPage() {
  const { t } = useTranslation();
  const localize = useLocalizedPath();
  return (
    <>
      <SEO titleKey="seo:notFound.title" descriptionKey="seo:notFound.description" path="/" />
      <div className="min-h-[60vh] flex items-center justify-center px-4 pt-24">
        <div className="text-center">
          <div className="font-display text-7xl font-medium text-primary mb-4 tracking-[-0.02em]">404</div>
          <h1 className="font-display text-2xl font-medium text-ink mb-2">{t('marketing:notFound.heading')}</h1>
          <p className="text-ink-muted mb-8 max-w-md">{t('seo:notFound.description')}</p>
          <Link to={localize('/')} className={`inline-flex items-center justify-center px-6 py-3 bg-primary text-on-primary rounded-md font-semibold hover:bg-primary-hover transition-colors duration-150 text-sm ${FOCUS_VISIBLE_RING}`}>
            {t('common:common.back')}
          </Link>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LOCALE LAYOUT -- syncs URL :locale param into i18next + <html>
   ═══════════════════════════════════════════════════════════════════════════ */

function LocaleLayout() {
  const params = useParams<{ locale?: string }>();
  const { i18n } = useTranslation();

  // Resolve the locale: explicit URL segment, or default (when bare-root route).
  // Invalid URL segments are caught by the wrapper route → NotFoundPage.
  const locale: SupportedLocale = params.locale && isSupportedLocale(params.locale)
    ? params.locale
    : DEFAULT_LOCALE;

  useEffect(() => {
    if (i18n.resolvedLanguage !== locale) {
      void i18n.changeLanguage(locale);
    }
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
  }, [locale, i18n]);

  return <Outlet />;
}

/** Validates the URL :locale segment; renders 404 if unsupported. */
function ValidatedLocaleLayout() {
  const params = useParams<{ locale?: string }>();
  if (params.locale && !isSupportedLocale(params.locale)) {
    return <NotFoundPage />;
  }
  return <LocaleLayout />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROUTES (shared across default + locale-prefixed)
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Page-level routes that render inside a LocaleLayout. Returned as an array of
 * <Route> elements so they can be embedded inside two outer wrapper routes —
 * one for the bare paths (English default at root) and one for the
 * `:locale`-prefixed paths — without rewrapping in a fragment (react-router
 * requires direct <Route> children inside parent <Route>).
 */
function getMarketingRoutes(): React.ReactNode[] {
  return [
    <Route key="index" index element={<LandingPage />} />,
    <Route key="terms" path="terms" element={<TermsPage />} />,
    <Route key="privacy" path="privacy" element={<PrivacyPage />} />,
    <Route key="dpa" path="dpa" element={<DpaPage />} />,
    <Route key="cookies" path="cookies" element={<CookiesPage />} />,
    <Route key="aup" path="aup" element={<AupPage />} />,
    <Route key="sla" path="sla" element={<SlaPage />} />,
    <Route key="about" path="about" element={<AboutPage />} />,
    <Route key="contact" path="contact" element={<ContactPage />} />,
    <Route key="careers" path="careers" element={<CareersPage />} />,
    <Route key="blog-post" path="blog/:slug" element={<BlogPostPage />} />,
    <Route key="blog" path="blog" element={<BlogPage />} />,
    <Route key="api-docs" path="api-docs" element={<ApiDocsPage />} />,
    <Route key="changelog" path="changelog" element={<ChangelogPage />} />,
    <Route key="compliance" path="compliance" element={<CompliancePage />} />,
    <Route key="security" path="security" element={<TrustPage />} />,
    <Route key="status" path="status" element={<StatusPage />} />,
    <Route key="not-found" path="*" element={<NotFoundPage />} />,
  ];
}

/* ═══════════════════════════════════════════════════════════════════════════
   APP
   ═══════════════════════════════════════════════════════════════════════════ */

export function App() {
  const activeSection = useActiveSection();

  return (
    <div id="top" className="min-h-screen bg-surface text-ink font-sans">
      <SkipToContent />
      <ScrollToTop />
      <Navigation activeSection={activeSection} />
      <main id="main-content">
        <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
          <Routes>
            {/* English default — no locale prefix */}
            <Route element={<LocaleLayout />}>{getMarketingRoutes()}</Route>
            {/* Locale-prefixed routes for the 21 non-default locales */}
            <Route path=":locale" element={<ValidatedLocaleLayout />}>
              {getMarketingRoutes()}
            </Route>
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}

